import { api, fetchWithAuth } from './api';
import type {
  Strategy,
  StrategyDetail,
  ModuleVersion,
  AIFeedback,
  RealityCheckEntry,
  StrategyAccount,
  StrategyPerformance,
  FeedbackEntry,
  ProfileKit,
  ProfileKitV2,
  KitPreset,
  KitGenerationLog,
  KitCounts,
  ProfileType,
  KitPlatform,
  AuditAction,
  AuditSuggestionAction,
  CurrentProfile,
  AuditImplementationScore,
  StrategyDocument,
  StrategyDocumentDetail,
  DocumentInsight,
  DocumentEvidenceResponse,
  BrandWithChannels,
  StrategyAsset,
  LogoOption,
  BootstrapAnalysis,
  BootstrapNicheRequest,
  BootstrapNicheResponse,
  CascadeStatus,
} from '../types';

export const strategyApi = {
  list: (status?: string) =>
    api.get<Strategy[]>(`/strategies${status ? `?status=${status}` : ''}`),

  create: (data: {
    name: string;
    niche?: string;
    category?: string;
    account_language?: string;
    source_expansion_run_id?: number;
    assign_insight_ids?: number[];
    suggestion_index?: number;
  }) => api.post<Strategy>('/strategies', data),

  get: (id: number) =>
    api.get<StrategyDetail>(`/strategies/${id}`),

  update: (id: number, data: Partial<StrategyDetail>) =>
    api.patch<Strategy>(`/strategies/${id}`, data),

  delete: (id: number) =>
    api.delete(`/strategies/${id}`),

  iterate: (id: number, module_id: string) =>
    api.post<{ version: number; outdated_flags: Record<string, boolean> }>(
      `/strategies/${id}/iterate`,
      { module_id }
    ),

  getVersions: (id: number, moduleId: string) =>
    api.get<ModuleVersion[]>(`/strategies/${id}/versions/${moduleId}`),

  restoreVersion: (id: number, moduleId: string, version: number) =>
    api.post<{ restored: boolean; data: unknown }>(`/strategies/${id}/versions/${moduleId}/restore`, { version }),

  realityCheck: (id: number, module_id: string, model?: string) =>
    api.post<AIFeedback>(`/strategies/${id}/reality-check`, { module_id, model }),

  getRealityChecks: (id: number, moduleId: string, limit = 5, offset = 0) =>
    api.get<{ items: RealityCheckEntry[]; total: number }>(
      `/strategies/${id}/reality-checks/${moduleId}?limit=${limit}&offset=${offset}`
    ),

  autofill: (id: number, body?: { start_from_module?: string }) =>
    api.post<{
      modules_data: Record<string, unknown>;
      cascade_status?: CascadeStatus;
      failed_module?: string | null;
      error_detail?: string | null;
    }>(`/strategies/${id}/autofill`, body ?? {}),

  autofillModule: (
    id: number,
    module_id: string,
    mode: 'regenerate' | 'improve',
    feedback?: string,
  ) =>
    api.post<{ module_data: Record<string, unknown> }>(
      `/strategies/${id}/autofill-module`,
      { module_id, mode, ...(feedback !== undefined ? { feedback } : {}) },
    ),

  // Refocus · guided focus-shift (preview → apply → strength-check)
  refocusPreview: (id: number, focus: string, documentIds?: number[]) =>
    api.post<{
      proposed_modules: Record<string, Record<string, unknown>>;
      per_module_mode: Record<string, 'regenerate' | 'improve'>;
    }>(`/strategies/${id}/refocus/preview`, {
      focus,
      ...(documentIds && documentIds.length ? { document_ids: documentIds } : {}),
    }),

  refocusApply: (id: number, proposedModules: Record<string, Record<string, unknown>>) =>
    api.post<{
      versioned_modules: Record<string, number>;
      modules_data: Record<string, unknown>;
    }>(`/strategies/${id}/refocus/apply`, { proposed_modules: proposedModules }),

  refocusStrengthCheck: (id: number) =>
    api.post<{ checks: Record<string, AIFeedback> }>(`/strategies/${id}/refocus/strength-check`, {}),

  // Import · merge a parsed strategy patch back in. Snapshots all modules first
  // (server-side) so the pre-import state is rollback-able in the version history.
  importModules: (id: number, modulesPatch: Record<string, Record<string, unknown>>) =>
    api.post<{ versioned_modules: Record<string, number>; modules_data: Record<string, unknown> }>(
      `/strategies/${id}/import`,
      { modules_patch: modulesPatch },
    ),

  advancePhase: (id: number) =>
    api.post<{ active_phase_index: number; phase_started_at: string }>(`/strategies/${id}/advance-phase`, {}),

  contentBootstrap: (id: number, currentData?: Record<string, unknown>) =>
    api.post<{ prefilled: Record<string, string | string[]> }>(`/strategies/${id}/content-bootstrap`, { current_data: currentData ?? {} }),

  phaseTransitionCheck: (id: number) =>
    api.get<{ suggest_transition: boolean; current_phase?: string; next_phase?: string; elapsed_weeks?: number; planned_weeks?: number }>(`/strategies/${id}/phase-transition-check`),

  visualIdentityAudit: (id: number) =>
    // POST: runs autofix (attach kit logo, derive palette, correct family lock)
    // and returns the resulting audit. Idempotent · every step skips when
    // values are already set or when the n>0 posterior guard blocks the change.
    api.post<{
      ok: boolean;
      issues: Array<{
        code: string;
        severity: 'warning' | 'info' | 'error';
        field: string;
        message: string;
        fix_hint: string;
      }>;
      resolved: {
        has_palette: boolean;
        has_logo: boolean;
        has_visual_style: boolean;
        has_brand_aesthetic: boolean;
        matched_family: string | null;
        niche: string;
      };
      autofix?: { applied: string[]; skipped: Array<{ step: string; reason: string }> };
    }>(`/strategies/${id}/visual-identity-autofix`, {}),

  // Module config
  getModulesConfig: () =>
    api.get<Record<string, { key_fields: string[]; defaults: Record<string, unknown>; upstream_deps: string[]; field_labels: Record<string, string> }>>('/strategies/modules/config'),

  // Bootstrap
  bootstrapAnalyze: (id: number, niche: string, briefing: string) =>
    api.post<BootstrapAnalysis>(`/strategies/${id}/bootstrap/analyze`, { niche, briefing }),

  bootstrapGenerateNiche: (id: number, data: BootstrapNicheRequest) =>
    api.post<BootstrapNicheResponse>(`/strategies/${id}/bootstrap/generate-niche`, data),

  translate: (id: number, targetLanguage: string) =>
    api.post<{ status: string; target_language: string }>(`/strategies/${id}/translate`, { target_language: targetLanguage }),

  assignAccount: (id: number, account_id: number) =>
    api.post<StrategyAccount>(`/strategies/${id}/accounts`, { account_id }),

  unassignAccount: (id: number, accountId: number) =>
    api.delete(`/strategies/${id}/accounts/${accountId}`),

  getAccounts: (id: number) =>
    api.get<{ accounts: StrategyAccount[]; performance: StrategyPerformance; brand: BrandWithChannels | null }>(
      `/strategies/${id}/accounts`
    ),

  addFeedback: (id: number, data: { type: string; notes?: string; account_id?: number }) =>
    api.post<{ id: number }>(`/strategies/${id}/feedback`, data),

  getFeedback: (id: number) =>
    api.get<FeedbackEntry[]>(`/strategies/${id}/feedback`),

  generateKits: (id: number) =>
    api.post<{ kits: ProfileKit[]; count: number }>(`/strategies/${id}/generate-kits`, {}),

  getKits: (id: number) =>
    api.get<ProfileKit[]>(`/strategies/${id}/kits`),

  getPerformance: (id: number) =>
    api.get<StrategyPerformance>(`/strategies/${id}/performance`),

  mergeEvolve: (strategy_ids: number[]) =>
    api.post<Strategy>('/strategies/merge-evolve', { strategy_ids }),

  // Kit v2 · Generation
  previewKitPrompt: (id: number, profile_type: ProfileType, preset_id?: number, custom_rules?: string) =>
    api.post<{ system_prompt: string; user_message: string }>(`/strategies/${id}/kits/preview-prompt`, { profile_type, preset_id, custom_rules }),

  generateKitV2: (id: number, data: { profile_type: ProfileType; preset_id?: number; custom_rules?: string; model?: string; prompt_override?: string; apply_learned?: boolean; mode?: string }) =>
    api.post<{ kit: ProfileKitV2; generation_id: number }>(`/strategies/${id}/kits/generate`, data),

  refineKit: (id: number, kit_id: number, feedback: string, model?: string) =>
    api.post<{ kit: ProfileKitV2; generation_id: number }>(`/strategies/${id}/kits/${kit_id}/refine`, { feedback, model }),

  refineKitField: (id: number, kit_id: number, field_name: string, feedback: string, model?: string) =>
    api.post<ProfileKitV2>(`/strategies/${id}/kits/${kit_id}/refine-field`, { field_name, feedback, model }),

  hasRefinements: (id: number) =>
    api.get<{ count: number }>(`/strategies/${id}/kits/has-refinements`),

  regenerateKit: (id: number, kit_id: number, model?: string) =>
    api.post<{ kit: ProfileKitV2; generation_id: number }>(`/strategies/${id}/kits/${kit_id}/regenerate`, { model }),

  // Kit v2 · CRUD
  getKitsV2: (id: number, starred_only?: boolean) =>
    api.get<{ kits: ProfileKitV2[]; counts: KitCounts }>(`/strategies/${id}/kits/v2${starred_only ? '?starred_only=true' : ''}`),

  getKitChain: (id: number, kit_id: number) =>
    api.get<{ chain: ProfileKitV2[] }>(`/strategies/${id}/kits/${kit_id}/chain`),

  updateKitV2: (id: number, kit_id: number, data: Partial<ProfileKitV2>) =>
    api.patch<ProfileKitV2>(`/strategies/${id}/kits/${kit_id}`, data),

  deleteKitV2: (id: number, kit_id: number) =>
    api.delete(`/strategies/${id}/kits/${kit_id}`),

  // Kit Presets
  getKitPresets: (id: number) =>
    api.get<KitPreset[]>(`/strategies/${id}/kits/presets`),

  createKitPreset: (id: number, data: Partial<KitPreset>) =>
    api.post<KitPreset>(`/strategies/${id}/kits/presets`, data),

  updateKitPreset: (id: number, preset_id: number, data: Partial<KitPreset>) =>
    api.patch<KitPreset>(`/strategies/${id}/kits/presets/${preset_id}`, data),

  deleteKitPreset: (id: number, preset_id: number) =>
    api.delete(`/strategies/${id}/kits/presets/${preset_id}`),

  // Kit History
  getKitHistory: (id: number, limit = 50, offset = 0) =>
    api.get<{ items: KitGenerationLog[]; total: number }>(`/strategies/${id}/kits/history?limit=${limit}&offset=${offset}`),

  // Category
  getCategories: () =>
    api.get<string[]>('/strategies/categories'),

  suggestCategory: (id: number) =>
    api.post<{ category: string }>(`/strategies/${id}/suggest-category`, {}),

  // Prompt Preview
  previewRealityCheck: (id: number, module_id: string) =>
    api.post<{ system_prompt: string; upstream_context: string; user_instructions: string }>(
      `/strategies/${id}/preview-reality-check`, { module_id }
    ),

  previewAutofill: (id: number, module_id: string) =>
    api.post<{ system_prompt: string; upstream_context: string; user_instructions: string }>(
      `/strategies/${id}/preview-autofill`, { module_id }
    ),

  // Posts (IGParasite connection)
  getStrategyPosts: (id: number, limit = 50, offset = 0) =>
    api.get<{ posts: Record<string, unknown>[] }>(`/strategies/${id}/posts?limit=${limit}&offset=${offset}`),

  getContentDistribution: (id: number) =>
    api.get<Record<string, number>>(`/strategies/${id}/content-distribution`),

  // Kit Rules & Visual Sync
  getKitRules: () => api.get<Record<string, { label: string; rules: string[]; examples: string[] }>>('/strategies/kit-rules'),

  checkVisualSyncNeeded: (strategyId: number) =>
    api.get<{ sync_needed: boolean }>(`/strategies/${strategyId}/kits/visual-sync-needed`),

  // Profile Audit
  auditProfileScreenshot: async (strategyId: number, profileImageBase64: string, feedImageBase64?: string, platform?: string, url?: string) => {
    const formData = new FormData();
    const profileBlob = base64ToBlob(profileImageBase64);
    formData.append('profile_image', profileBlob, 'profile.jpg');
    if (feedImageBase64) {
      const feedBlob = base64ToBlob(feedImageBase64);
      formData.append('feed_image', feedBlob, 'feed.jpg');
    }
    if (platform) formData.append('platform', platform);
    if (url) formData.append('url', url);
    const res = await fetchWithAuth(`/api/strategies/${strategyId}/kits/audit-screenshot`, {
      method: 'POST',
      body: formData,
    });
    return res.json();
  },

  getLpScreenshot: (strategyId: number, url: string) =>
    api.post<{ screenshot_base64: string }>(`/strategies/${strategyId}/kits/lp-screenshot`, { url }),

  listAudits: (strategyId: number, platform?: string) =>
    api.get<unknown[]>(`/strategies/${strategyId}/kits/audits${platform ? `?platform=${encodeURIComponent(platform)}` : ''}`),

  getAudit: (strategyId: number, auditId: number) =>
    api.get<unknown>(`/strategies/${strategyId}/kits/audits/${auditId}`),

  // Current Profile
  getCurrentProfile: (strategyId: number, platform: string = 'instagram') =>
    api.get<CurrentProfile>(`/strategies/${strategyId}/current-profile?platform=${encodeURIComponent(platform)}`),

  updateCurrentProfile: (strategyId: number, data: Partial<CurrentProfile>, platform: string = 'instagram') =>
    api.put<CurrentProfile>(`/strategies/${strategyId}/current-profile?platform=${encodeURIComponent(platform)}`, data),

  adoptKitField: (strategyId: number, kitId: number, fieldName: string, value: unknown) =>
    api.post<CurrentProfile>(`/strategies/${strategyId}/kits/${kitId}/adopt-field`, { field_name: fieldName, value }),

  removeProfileField: (strategyId: number, fieldName: string) =>
    api.delete<CurrentProfile>(`/strategies/${strategyId}/current-profile/field/${fieldName}`),

  // Audit Actions
  saveAuditAction: (strategyId: number, auditId: number, body: { field_name: string; action: AuditAction; custom_value?: string; target_kit_id?: number; original_suggestion?: string }) =>
    api.post<AuditSuggestionAction[]>(`/strategies/${strategyId}/kits/audits/${auditId}/actions`, body),

  getAuditActions: (strategyId: number, auditId: number) =>
    api.get<AuditSuggestionAction[]>(`/strategies/${strategyId}/kits/audits/${auditId}/actions`),

  getAuditScore: (strategyId: number, platform?: string) =>
    api.get<AuditImplementationScore>(`/strategies/${strategyId}/kits/audit-score${platform ? `?platform=${encodeURIComponent(platform)}` : ''}`),

  // Documents
  uploadDocuments: async (strategyId: number, files: File[]): Promise<StrategyDocument[]> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const res = await fetchWithAuth(`/api/strategies/${strategyId}/documents`, {
      method: 'POST',
      body: formData,
    });
    return res.json();
  },

  listDocuments: async (strategyId: number): Promise<StrategyDocument[]> => {
    return api.get<StrategyDocument[]>(`/strategies/${strategyId}/documents`);
  },

  getDocument: async (strategyId: number, docId: number): Promise<StrategyDocumentDetail> => {
    return api.get<StrategyDocumentDetail>(`/strategies/${strategyId}/documents/${docId}`);
  },

  deleteDocument: async (strategyId: number, docId: number): Promise<void> => {
    return api.delete(`/strategies/${strategyId}/documents/${docId}`);
  },

  reprocessDocument: async (strategyId: number, docId: number): Promise<StrategyDocument> => {
    return api.post<StrategyDocument>(`/strategies/${strategyId}/documents/${docId}/reprocess`, {});
  },

  importDocumentUrl: async (strategyId: number, url: string): Promise<StrategyDocument[]> => {
    return api.post<StrategyDocument[]>(`/strategies/${strategyId}/documents/import-url`, { url });
  },

  listDocumentInsights: async (strategyId: number, docId: number): Promise<DocumentInsight[]> => {
    return api.get<DocumentInsight[]>(`/strategies/${strategyId}/documents/${docId}/insights`);
  },

  listStrategyInsights: async (strategyId: number, activeOnly = false): Promise<DocumentInsight[]> => {
    return api.get<DocumentInsight[]>(`/strategies/${strategyId}/insights${activeOnly ? '?active_only=true' : ''}`);
  },

  setInsightActive: async (strategyId: number, insightId: number, isActive: boolean): Promise<void> => {
    return api.patch(`/strategies/${strategyId}/insights/${insightId}`, { is_active: isActive });
  },

  setInsightAnonymize: async (strategyId: number, insightId: number, entity: string | null): Promise<void> => {
    return api.patch(`/strategies/${strategyId}/insights/${insightId}/anonymize`, { anonymize_entity: entity });
  },

  reextractDocumentInsights: async (strategyId: number, docId: number): Promise<{ ok: boolean; insight_count: number }> => {
    return api.post(`/strategies/${strategyId}/documents/${docId}/reextract-insights`, {});
  },

  listNiches: async (): Promise<{ items: Array<{ id: number; name: string; parent_niche_id: number | null; description: string | null }> }> => {
    return api.get('/strategies/niches');
  },

  updateDocumentNiche: async (
    strategyId: number,
    docId: number,
    body: { niche_id: number | null; default_scope?: 'private' | 'niche' | 'account' },
  ): Promise<{ ok: boolean }> => {
    return api.patch(`/strategies/${strategyId}/documents/${docId}/niche`, body);
  },

  setInsightScope: async (
    strategyId: number,
    insightId: number,
    body: {
      scope_level: 'private' | 'niche' | 'account';
      niche_ids?: number[];
      strategy_ids?: number[];
      expected_updated_at?: string;
    },
  ): Promise<{ ok: boolean; updated_at?: string }> => {
    return api.patch(`/strategies/${strategyId}/insights/${insightId}/scope`, body);
  },

  bulkSetChapterScope: async (
    strategyId: number,
    docId: number,
    chapterIndex: number,
    scopeLevel: 'private' | 'niche' | 'account',
  ): Promise<{ ok: boolean; updated: number }> => {
    return api.post(`/strategies/${strategyId}/documents/${docId}/chapter-scope`, {
      chapter_index: chapterIndex,
      scope_level: scopeLevel,
    });
  },

  updateInsightChapterLabel: async (
    strategyId: number,
    insightId: number,
    chapterLabel: string | null,
  ): Promise<{ ok: boolean }> => {
    return api.patch(`/strategies/${strategyId}/insights/${insightId}/chapter-label`, {
      chapter_label: chapterLabel,
    });
  },

  getCoverageStats: async (strategyId: number): Promise<{ items: Array<{
    doc_id: number; original_filename: string; niche_id: number | null;
    default_scope: 'private' | 'niche' | 'account'; coverage_baseline_at: string | null;
    total: number; used: number;
  }> }> => {
    return api.get(`/strategies/${strategyId}/coverage-stats`);
  },

  resetDocumentCoverage: async (strategyId: number, docId: number): Promise<{ ok: boolean }> => {
    return api.post(`/strategies/${strategyId}/documents/${docId}/reset-coverage`, {});
  },

  updateToneStyle: async (
    strategyId: number,
    partial: Partial<{
      aggressiveness: number;
      aspectFocus: 'pain' | 'desire' | 'transformation';
      toneFormality: number;
      mehrwert: number;
      metricAccumulation: boolean;
    }>,
  ): Promise<{ ok: boolean; tone_style_preferences: {
    aggressiveness: number; aspectFocus: 'pain' | 'desire' | 'transformation';
    toneFormality: number; mehrwert: number; metricAccumulation: boolean;
  } }> => {
    return api.patch(`/strategies/${strategyId}/tone-style`, partial);
  },

  previewDocumentEvidence: async (
    strategyId: number,
    documentIds: number[],
    topicAngle?: string,
    tensionCategory?: string | null,
    topK = 6,
    signal?: AbortSignal,
  ): Promise<DocumentEvidenceResponse> => {
    if (documentIds.length === 0) {
      return { items: [], missing_insight_doc_ids: [] };
    }
    const params = new URLSearchParams();
    params.set('document_ids', documentIds.join(','));
    if (topicAngle) params.set('topic_angle', topicAngle);
    if (tensionCategory) params.set('tension_category', tensionCategory);
    params.set('top_k', String(topK));
    const res = await fetchWithAuth(
      `/api/strategies/${strategyId}/document-evidence?${params.toString()}`,
      { signal },
    );
    if (!res.ok) throw new Error(`document-evidence ${res.status}`);
    return res.json();
  },

  // Strategy Assets
  getStrategyAssets: (strategyId: number, platform?: KitPlatform) =>
    api.get<StrategyAsset[]>(`/strategies/${strategyId}/assets${platform ? `?platform=${platform}` : ''}`),

  generateStrategyLogo: (strategyId: number, prompt: string, platform: KitPlatform = 'instagram', style?: string) =>
    api.post<{ options: LogoOption[] }>(`/strategies/${strategyId}/assets/logo/generate`, { prompt, platform, ...(style ? { style } : {}) }),

  saveStrategyLogo: (strategyId: number, svg: string, platform: KitPlatform = 'instagram') =>
    api.post<StrategyAsset>(`/strategies/${strategyId}/assets/logo/save`, { svg, platform }),

  generateHighlightCovers: (strategyId: number, themes: string[], platform: KitPlatform = 'instagram') =>
    api.post<{ results: Array<{ theme_name: string; options: LogoOption[] }> }>(
      `/strategies/${strategyId}/assets/highlight-covers/generate`,
      { themes, platform }
    ),

  saveHighlightCover: (strategyId: number, theme_name: string, svg: string, platform: KitPlatform = 'instagram') =>
    api.post<StrategyAsset>(`/strategies/${strategyId}/assets/highlight-covers/save`, { theme_name, svg, platform }),

  deleteStrategyAsset: (strategyId: number, assetId: number) =>
    api.delete(`/strategies/${strategyId}/assets/${assetId}`),
};

function base64ToBlob(base64: string): Blob {
  const parts = base64.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const byteString = atob(parts.length > 1 ? parts[1] : parts[0]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mime });
}
